import React, { useCallback, useReducer } from 'react';
import LeadContext from './leadContext';
import LeadReducer from './leadReducer';
import api from '../../api/api';
import calls from '../../api/apiCalls';
import {
    GET_LEADS,
    GET_LEAD,
    UPDATE_LEAD,
    DELETE_LEAD,
    CREATE_LEAD,
    CLEAR_STATE,
    SET_LOADING,
    GET_LEADS_BY_STORE,
    GET_LEADS_BY_USER,
    CALL_USER,
    LOAD_CSV,
    ASSIGN_AGENTS,
    ASSIGN_STATUSES,
    ADD_COMMENT,
    ADD_APPOINTMENT,
    UPDATE_TASK_FROM_LEAD,
    DELETE_COMMENT,
    LOAD_CORRECT_CSV,
    GET_TOKEN_UPDATE,
    CREATE_COMMENT,
    SET_SUCCESS,
    MARK_AS_DUPLICATED,
    SEND_MAIL,
    SEND_WHATSAPP,
    CLEAR_ERRORS,
    UPLOAD_EXCEL,
    MAKE_AI_CALL,
    SEND_PRESENTATION_CARD,
    REMOVE_FROM_CALENDAR,
} from '../types';
import { HEADERS } from '../../constants/headers';
import { ApiV2 } from 'src/api/apiv2';
import {
    sendWhatsApp,
    sendEmail,
} from 'src/shared/communications';
import { mapTemplatePayloadToSendWhatsAppDTO } from 'src/modules/conversations/helpers/mapMessagePayloadToSendWhatsAppDTO';
import { ADD } from 'src/constants/events/files';
import { uploadImageToS3 } from '../utils/uploadImageToS3';
import { AWS_S3 } from 'src/constants/crm/aws';
import axios from 'axios';
import { getSignatureImage } from 'src/utils/evaluation';
import { handleError } from '../utils/handleError';
import { useTranslation } from 'react-i18next';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import { useHistory } from 'react-router-dom';
import { SERVICE_LEAD_TYPE } from 'src/constants/lead/leadTypes';

const LeadState = (props) => {
    const history = useHistory();
    const { customAlert } = SnackbarAlert();
    const { t } = useTranslation();

    const initialState = {
        leads: [],
        lead: {},
        leadType: null,
        loading: false,
        error: null,
        analytics: [],
        lastLeads: [],
        labels: null,
        count: null,
        callToken: null,
        excelResponse: [],
        tokenUpdate: null,
        success: false,
        duplicateLeadsId: null,
        errors: [],
        calendarInfo: null,
    };

    const [state, dispatch] = useReducer(LeadReducer, initialState);

    const clearErrors = () =>
        setTimeout(() => dispatch({ type: CLEAR_ERRORS }), 3000);

    const setLead = useCallback(
        (lead) => dispatch({ type: GET_LEAD, payload: lead }),
        []
    );

    const sendPresentationCard = async (values) => {
        setLoading();
        try {
            const res = await api.post(
                `/mails/presentation-card`,
                { ...values },
                HEADERS()
            );
            dispatch({
                type: SEND_PRESENTATION_CARD,
                payload: res.data.data,
                successMessage: 'PresentationCard.PresentationCardSent',
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const makeAICall = async (leadId) => {
        setLoading();
        try {
            const response = await api.post(
                `/leads/makeAICall`,
                { leadId },
                HEADERS()
            );

            dispatch({
                type: MAKE_AI_CALL,
                successMessage: 'Calls.AICallSent',
            });
            return response.data.data;
        } catch (error) {
            dispatch(handleError(error));
            return false;
        }
    };

    const assignAgentsBulkActions = async ({ leads, agent, callback }) => {
        setLoading();
        try {
            await api.post(`/leads/assignAgent`, { leads, agent }, HEADERS());
            callback();
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const assignStatusBulkActions = async ({
        leads,
        status,
        substatus,
        isContacted,
        callback,
    }) => {
        setLoading();
        try {
            await api.post(
                `/leads/assignStatus`,
                { leads, status, substatus, isContacted },
                HEADERS()
            );
            callback();
        } catch (error) {
            dispatch(handleError(error));
            clearErrors();
        }
    };

    const searchLeadsByFilters = async (filters) => {
        setLoading();
        try {
            const res = await api.post(
                `/leads/search`,
                { ...filters },
                HEADERS()
            );

            dispatch({
                type: GET_LEADS,
                payload: res.data.results,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
            clearErrors();
        }
    };

    /**
     * Will send a whatsapp template to the lead
     *
     * @param {*} template
     * @param {*} leadId
     */
    const sendTemplateV2 = async (template, leadId) => {
        setLoading();
        try {
            const dto = mapTemplatePayloadToSendWhatsAppDTO(template, leadId);
            const res = await sendWhatsApp(dto, {
                transport: ApiV2,
                endpoint: '/leads/whatsApp',
                config: HEADERS(),
            });
            dispatch({
                type: SEND_WHATSAPP,
                payload: res.data.response,
                successMessage: 'TwilioTemplates.Send',
            });
            return true;
        } catch (error) {
            dispatch(handleError(error));
            return false;
        }
    };
    /**
     * Will send the documentation to the lead (uses shared sendEmail).
     *
     * @param {*} mail
     */
    const sendDocumentationV2 = async (mail) => {
        setLoading();
        try {
            const { attachments = [] } = mail;

            let files = [];
            for (let i = 0; i < attachments.length; i++) {
                const currentAttachment = attachments[i];
                const { key } = await uploadImageToS3(currentAttachment);
                files.push({
                    title: key.split('.').slice(0, -1).join('.'),
                    file: AWS_S3 + key,
                    id: i,
                });
            }

            const dto = {
                to: mail.email,
                subject: mail.subject,
                body: mail.message,
                template: mail.template,
                attachments: files,
                documents: mail.documents,
                storeId: mail.store,
                userId: mail.user,
                leadId: mail.lead,
                isAssistantGenerated: mail.isAssistantGenerated,
            };
            const res = await sendEmail(dto, {
                transport: ApiV2,
                endpoint: '/mailing/documentation',
                config: HEADERS(),
            });
            let snackbarMessage = 'SnackBar.DocumentationSent';
            if (files.length <= 0) snackbarMessage = 'SnackBar.EmailSent';
            dispatch({
                type: SEND_MAIL,
                payload: res.data.response,
                successMessage: snackbarMessage,
            });
            return true;
        } catch (error) {
            dispatch(handleError(error));
            return false;
        }
    };

    /**
     * Will create a task for the specified lead
     *
     * @param {*} values comment information
     * @return {*}
     */
    const createTaskV2 = async (values) => {
        try {
            setLoading();
            const res = await ApiV2.post(
                `tasks/create`,
                { ...values },
                HEADERS()
            );
            if (res.data.calendarInfo) {
                dispatch({
                    type: REMOVE_FROM_CALENDAR,
                    payload: res.data.calendarInfo,
                });
            } else {
                dispatch({
                    type: CREATE_COMMENT,
                    payload: res.data.response,
                    successMessage: 'SnackBar.NewTask',
                });
            }
            return true;
        } catch (error) {
            dispatch(handleError(error));
            return false;
        }
    };

    /**
     * Will reject the lead
     *
     * @param {*} values
     */
    const rejectLeadV2 = async (values) => {
        try {
            setLoading();

            const res = await ApiV2.post(
                `leads/reject`,
                { ...values },
                HEADERS()
            );

            if (res.data.calendarInfo) {
                dispatch({
                    type: REMOVE_FROM_CALENDAR,
                    payload: res.data.calendarInfo,
                });
            } else {
                dispatch({
                    type: CREATE_COMMENT,
                    payload: res.data.response,
                    successMessage: 'SnackBar.RejectLead',
                });
            }

            setSuccess();
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Delete lead from state
    const deleteCommentLead = async (commentId) =>
        dispatch({ type: DELETE_COMMENT, payload: commentId });

    const getTokenUpdate = async (leadId) => {
        try {
            const res = await api.get(`/leads/token/${leadId}`, HEADERS());
            dispatch({ type: GET_TOKEN_UPDATE, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Leads
    const getLeads = async (values) => {
        setLoading();

        try {
            const res = await api.post(
                `/leads/AdvancedResults`,
                { ...values },
                HEADERS()
            );
            dispatch({
                type: GET_LEADS,
                payload: res.data.data,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getLeadsV2 = async (values, leadType) => {
        setLoading();
        try {
            const res = await api.post(
                `/leads/AdvancedResultsV2`,
                { ...values },
                HEADERS()
            );
            dispatch({
                type: GET_LEADS,
                leadType,
                payload: res.data.data,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Leads
    const startCron = async (status) => {
        try {
            await api.post(`/leads/sendAlerts`, { start: status }, HEADERS);
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Leads
    const getLeadsByStore = async (
        storequery,
        pagination,
        tabs,
        query,
        typeQuery,
        multiStore
    ) => {
        setLoading();
        try {
            let res;
            if (!query) query = '';

            if (pagination) {
                if (tabs.includes('temperature')) {
                    let temp = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&rating=${temp[1]}&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                } else if (tabs.includes('status')) {
                    let status = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&status=${status[1]}&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                } else if (tabs.includes('subStatus')) {
                    let substatus = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&substatus=${substatus[1]}&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                } else if (tabs.includes('contacted')) {
                    let contacted = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&isContacted=${contacted[1]}&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                } else if (tabs.includes('assigned')) {
                    let assigned = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&assigned=${assigned[1]}&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                } else {
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&searchText=${query}${storequery}${typeQuery}&validation=1${multiStore}`,
                        HEADERS()
                    );
                }
            }

            dispatch({
                type: GET_LEADS_BY_STORE,
                payload: res.data.data,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Leads by User
    const getLeadsByUser = async (
        userId,
        pagination,
        tabs,
        query,
        typeQuery
    ) => {
        setLoading();
        try {
            let res;
            if (!query) query = '';

            if (pagination) {
                if (tabs.includes('temperature')) {
                    let temp = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&rating=${temp[1]}&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                } else if (tabs.includes('status')) {
                    let status = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&status=${status[1]}&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                } else if (tabs.includes('subStatus')) {
                    let substatus = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&substatus=${substatus[1]}&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                } else if (tabs.includes('contacted')) {
                    let contacted = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&isContacted=${contacted[1]}&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                } else if (tabs.includes('assigned')) {
                    let assigned = tabs.split('.');
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&assigned=${assigned[1]}&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                } else {
                    res = await api.get(
                        `/leads?page=${pagination.page}&limit=${pagination.limit}&searchIndex=name-email-make-phone-agent-source-vehicle-store&searchText=${query}&agent=${userId}${typeQuery}&validation=1`,
                        HEADERS()
                    );
                }
            }

            dispatch({
                type: GET_LEADS_BY_USER,
                payload: res.data.data,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const setAsDuplicated = async (leadId) => {
        setLoading();

        try {
            const res = await ApiV2.post(
                `/leads/mark-as-duplicated/${leadId}`,
                {},
                HEADERS()
            );

            dispatch({
                type: MARK_AS_DUPLICATED,
                payload: res.data.response,
                successMessage: 'SnackBar.Duplicated',
            });
            return res.data.response;
        } catch (error) {
            dispatch(handleError(error));
            throw error;
        }
    };

    //Get Single Item by ID
    const getLead = async (leadId) => {
        setLoading();
        clearErrors();
        try {
            if (leadId && String(leadId) !== 'undefined') {
                const res = await api.get(`/leads/${leadId}`, HEADERS());
                dispatch({
                    type: GET_LEAD,
                    payload: res.data.data,
                });
            } else {
                clearState();
            }
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Update task from Lead
    const updateTaskFromLead = async (taskId) =>
        dispatch({ type: UPDATE_TASK_FROM_LEAD, payload: taskId });

    const uploadCsv = async (data) => {
        setLoading();
        try {
            await api.post(`/utils/uploadCsv`, data, HEADERS());
            dispatch({ type: LOAD_CORRECT_CSV });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const validateExcel = async (data) => {
        setLoading();
        try {
            let res = await api.post(`/utils/validateExcel`, data, HEADERS());
            dispatch({ type: LOAD_CSV, payload: res.data.errores });
        } catch (error) {
            dispatch(handleError(error));
        }
        clearErrors();
    };

    const createLeadByExcel = async (data) => {
        setLoading();
        try {
            let res = await api.post(`/leads/excel`, data, HEADERS());
            dispatch({
                type: UPLOAD_EXCEL,
                payload: res.data.data,
                successMessage: 'SnackBar.ExcelUploaded',
                errors: res.data.errorLeads,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getCreditBureau = async (leadId) => {
        setLoading();
        try {
            const res = await api.get(`/leads/bc-score/${leadId}`, HEADERS());
            dispatch({
                type: UPDATE_LEAD,
                payload: res.data.data,
                successMessage: 'SnackBar.LeadUpdate',
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Update Lead
    const updateLead = async (lead, leadId) => {
        setLoading();
        try {
            const res = await api.put(
                `/leads/${leadId}`,
                { ...lead },
                HEADERS()
            );
            const updatedLead = res.data.data;
            dispatch({
                type: UPDATE_LEAD,
                payload: updatedLead,
                successMessage: 'SnackBar.LeadUpdate',
            });
            customAlert(t('SnackBar.LeadUpdate'), 'success');
            history.push(`/management/leads/${leadId}`);
            return updatedLead;
        } catch (error) {
            dispatch(handleError(error));
            return undefined;
        }
    };

    const updatedEvaluationSignatures = async (lead, leadId) => {
        setLoading();
        try {
            const { evaluation } = lead;
            const updateSignature = getSignatureImage(evaluation);
            const updatedLeadEvaluation = {
                evaluation: {
                    ...lead.evaluation,
                    signs: updateSignature,
                },
            };
            const res = await api.put(
                `/leads/${leadId}`,
                updatedLeadEvaluation,
                HEADERS()
            );
            dispatch({
                type: UPDATE_LEAD,
                payload: res.data.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const updateFiles = async ({ files = [], event = ADD, leadId, index }) => {
        setLoading();
        try {
            let uploadConfig;
            let dataKeys = [];

            for (let i = 0; i < files.length; i++) {
                uploadConfig = await api.post(
                    '/uploads/publicImage',
                    {
                        type: files[i].type,
                        fileName: files[i].name,
                    },
                    HEADERS()
                );

                await axios.put(uploadConfig.data.url, files[i], {
                    headers: {
                        'Content-Type': files[i] ? files[i].type : null,
                    },
                });

                dataKeys.push(`${AWS_S3}${uploadConfig.data.key}`);
            }

            const res = await api.put(
                `/leads/evidences/${leadId}`,
                {
                    images: dataKeys,
                    event,
                    index,
                },
                HEADERS()
            );

            dispatch({
                type: UPDATE_LEAD,
                payload: res.data.data,
                successMessage: 'SnackBar.LeadUpdate',
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const updateLeadDocumentation = async (file, leadId) => {
        setLoading();
        try {
            let uploadConfig;
            let dataKeys = [];
            let res;

            for (let i = 0; i < file.length; i++) {
                uploadConfig = await api.post(
                    '/uploads/publicImage',
                    {
                        type: file[i].type,
                        fileName: file[i].name,
                    },
                    HEADERS()
                );
                await axios.put(uploadConfig.data.url, file[i], {
                    headers: {
                        headers: {
                            'Content-Type': file[i] ? file[i].type : null,
                        },
                    },
                });
                dataKeys.push(uploadConfig.data.key);
            }

            res = await api.put(`/leads/documentation/${leadId}`, {
                data: dataKeys,
            });
            dispatch({
                type: UPDATE_LEAD,
                payload: res.data.data,
                successMessage: 'SnackBar.LeadUpdate',
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Delete Lead
    const deleteLead = async (leadId) => {
        setLoading();
        try {
            const res = await api.delete(`/leads/${leadId}`, HEADERS());
            dispatch({ type: DELETE_LEAD, payload: res.data.deletedId });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Create Lead
    const createLead = async (lead, options = {}) => {
        clearState();
        setLoading();
        try {
            const res = await api.post(`/leads`, { ...lead }, HEADERS());
            const newLead = res.data.data;
            dispatch({ type: CREATE_LEAD, payload: newLead });
            customAlert(t('SnackBar.LeadCreated'), 'success');
            if (options.redirect === false) return newLead;
            let url = '/management/leads';
            if (lead.type === SERVICE_LEAD_TYPE) url = '/management/service-clients';
            history.push(`${url}/${newLead._id}`);
            return newLead;
        } catch (error) {
            const errorDispatch = handleError(error);
            dispatch(errorDispatch);
        }
    };

    const assignAgents = async (leads, agent, currentTab) => {
        setLoading();
        try {
            const res = await api.post(
                `/leads/assignAgent`,
                { leads, agent },
                HEADERS()
            );
            dispatch({
                type: ASSIGN_AGENTS,
                payload: res.data.data,
                tab: currentTab,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const assignStatus = async (
        leads,
        status,
        substatus,
        isContacted,
        currentTab
    ) => {
        setLoading();
        try {
            const res = await api.post(
                `/leads/assignStatus`,
                { leads, status, substatus, isContacted },
                HEADERS()
            );
            dispatch({
                type: ASSIGN_STATUSES,
                payload: res.data.data,
                tab: currentTab,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const AdvancedResults = async (pagination, query) => {
        setLoading();
        try {
            const res = await api.get(
                `/leads?page=${pagination.page}&limit=${pagination.limit}${query}&searchType=and&`,
                HEADERS()
            );
            dispatch({
                type: GET_LEADS,
                payload: res.data.data,
                count: res.data.pagination.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const generateToken = async (store) => {
        try {
            const res = await calls.post('/calls/token', { store }, HEADERS());
            dispatch({ type: CALL_USER, payload: res.data.token });
            return res.data.token;
        } catch (error) {
            dispatch(handleError(error));
            return null;
        }
    };

    //Clear State
    const clearState = () => dispatch({ type: CLEAR_STATE });

    const addComment = (comment) => {
        if (comment && comment.action && comment.action.includes('sold'))
            return;
        dispatch({ type: ADD_COMMENT, payload: comment });
    };
    const addAppointment = (appointment) =>
        dispatch({ type: ADD_APPOINTMENT, payload: appointment });

    //Set Loading
    const setLoading = () => dispatch({ type: SET_LOADING });

    const setSuccess = () => {
        setTimeout(function () {
            dispatch({ type: SET_SUCCESS });
        }, 1000);
    };

    return (
        <LeadContext.Provider
            value={{
                ...state,
                updateTaskFromLead,
                startCron,
                generateToken,
                getLeads,
                getLead,
                setLead,
                updateLead,
                updatedEvaluationSignatures,
                deleteLead,
                createLead,
                clearState,
                setLoading,
                validateExcel,
                getLeadsByStore,
                getLeadsByUser,
                assignAgents,
                updateLeadDocumentation,
                uploadCsv,
                AdvancedResults,
                assignStatus,
                addComment,
                addAppointment,
                deleteCommentLead,
                getTokenUpdate,
                getCreditBureau,
                updateFiles,
                getLeadsV2,
                setAsDuplicated,
                createLeadByExcel,

                createTaskV2,
                rejectLeadV2,
                sendDocumentationV2,
                sendTemplateV2,

                searchLeadsByFilters,
                assignStatusBulkActions,
                assignAgentsBulkActions,

                makeAICall,
                sendPresentationCard,
            }}
        >
            {props.children}
        </LeadContext.Provider>
    );
};

export default LeadState;
